import React, { useContext, useEffect, useState } from "react";
import { secured } from "react-abac";
import InfiniteScroll from "react-infinite-scroll-component";
import { AnimClassEnum, AnimOrderEnum, AppEnum, AuthoritiesEnum, CollapsableTypeEnum, CRUDActionEnum, InvitationStatusEnum, StatusStateEnum } from "~/types/enums";
import RestrictedSection from "~/components/RestrictedSection";
import { Badge, Box, Button, Card, Dropdown, Grid, Icon, InputSelect, InputText, Modal, Picture, Section, Spinner, Text } from "~/components-shared";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import CoreContext from "~/lib/context";
import useAbacExt from "~/hooks/abac";
import manageCompanySelectors from "~/store/manageCompanies/selectors";
import ErrorBoundaryCard from "~/components/ErrorBoundaryCard";
import lists from "~/lib/lists";
import manageCompaniesActionCreators from "~/store/manageCompanies/actions";
import moment from "moment";
import styled, { useTheme } from "styled-components";
import ManageCompanyCreateModal from "~/components/ManageCompanyCreateModal";
import ManageCompanyUpdateModal from "~/components/ManageCompanyUpdateModal";
import ManageCompanyDeleteModal from "~/components/ManageCompanyDeleteModal";
import ManageCompanyRoleCreateModal from "~/components/ManageCompanyRoleCreateModal";
import ManageCompanyRoleUpdateModal from "~/components/ManageCompanyRoleUpdateModal";
import { ManageCompaniesActionEnum } from "~/store/manageCompanies/types";
import ManageCompanyRoleDeleteModal from "~/components/ManageCompanyRoleDeleteModal";
import ManageCompanyInviteMembersModal from "~/components/ManageCompanyInviteMembersModal";
import ManageCompanyDuplicateModal from "~/components/ManageCompanyDuplicateModal";
import Collapsable from "~/components/Collapsable";
import CompanyManagementMemberCard from "~/components/CompanyManagementMemberCard";
import ManageCompanyMemberUpdateModal from "~/components/ManageCompanyMemberUpdateModal";
import ManageCompanyMemberDeleteModal from "~/components/ManageCompanyMemberDeleteModal";
import Markdown from "~/components/Markdown";
import api from "~/lib/api";
import { helpers } from "~/lib/helpers";
const StyledGrid = styled(Grid)`
  border: 1px solid ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.borders.primary;
}};
  border-radius: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.shape.borderRadius.primary;
}};
  word-wrap: break-word;

  & > * {
    height: 100%;

    &:not(:last-child) {
      border-right: 1px solid ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.borders.primary;
}};
    }
`;
const Actions = styled(Box)`
  position: absolute;
  top: 15px;
  right: 10px;
`;
const scrollPaneStyle = {
  overflow: "initial",
  height: "initial"
};
const CompanyManagement = () => {
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const {
    setSidebar
  } = useContext(CoreContext);
  const [lineItemActionsLoading, setLineItemActionsLoading] = useState(false);
  const [topicTrendActionsLoading, setTopicTrendActionsLoading] = useState(false);
  const companies = useSelector(manageCompanySelectors.getCompanies);
  const status = useSelector(manageCompanySelectors.getStatus);
  const action = useSelector(manageCompanySelectors.getAction);
  const sort = useSelector(manageCompanySelectors.getSort);
  const filter = useSelector(manageCompanySelectors.getFilter);
  const {
    hasAfter
  } = useSelector(manageCompanySelectors.getPagination);
  const {
    userHas,
    userHasOneOf
  } = useAbacExt();
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(manageCompaniesActionCreators.fetchStarted());
    }
  }, [dispatch, status]);
  useEffect(() => {
    setSidebar(undefined);
  }, [setSidebar]);
  const handleCreate = () => {
    dispatch(manageCompaniesActionCreators.actionChanged(CRUDActionEnum.CREATE, null));
  };
  const handleSort = newSort => {
    dispatch(manageCompaniesActionCreators.sorted(newSort));
  };
  const handleFilter = newFilter => {
    dispatch(manageCompaniesActionCreators.filtered(newFilter));
  };
  const handleEdit = id => {
    dispatch(manageCompaniesActionCreators.actionChanged(CRUDActionEnum.UPDATE, id));
  };
  const handleDelete = id => {
    dispatch(manageCompaniesActionCreators.actionChanged(CRUDActionEnum.DELETE, id));
  };
  const handleDuplicate = id => {
    dispatch(manageCompaniesActionCreators.actionChanged(ManageCompaniesActionEnum.DUPLICATE, id));
  };
  const handleLoadMore = () => {
    dispatch(manageCompaniesActionCreators.loadedMore());
  };
  const handleInviteMembers = companyId => {
    dispatch(manageCompaniesActionCreators.actionChanged(ManageCompaniesActionEnum.INVITE_MEMBERS, companyId));
  };
  const handleCreateRole = companyId => {
    dispatch(manageCompaniesActionCreators.actionChanged(ManageCompaniesActionEnum.CREATE_ROLE, companyId));
  };
  const handleUpdateRole = (companyId, roleId) => {
    dispatch(manageCompaniesActionCreators.actionChanged(ManageCompaniesActionEnum.UPDATE_ROLE, companyId, roleId));
  };
  const handleDeleteRole = (companyId, roleId) => {
    dispatch(manageCompaniesActionCreators.actionChanged(ManageCompaniesActionEnum.DELETE_ROLE, companyId, roleId));
  };
  const handleDownloadLineItemActions = companyId => {
    setLineItemActionsLoading(true);
    api.fetchProgressFlowItemActions(companyId).then(response => {
      const csvBlob = new Blob([response], {
        type: "text/csv;charset=utf-8;"
      });
      const url = window.URL.createObjectURL(csvBlob);
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", "line-item-actions.csv");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }).finally(() => {
      setLineItemActionsLoading(false);
    });
  };
  const handleDownloadTopicTrendActions = companyId => {
    setTopicTrendActionsLoading(true);
    api.fetchTopicTrendActions(companyId).then(response => {
      const csvBlob = new Blob([response], {
        type: "text/csv;charset=utf-8;"
      });
      const url = window.URL.createObjectURL(csvBlob);
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", "topic-trend-actions.csv");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }).finally(() => {
      setTopicTrendActionsLoading(false);
    });
  };
  return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(InfiniteScroll, {
    style: scrollPaneStyle,
    dataLength: companies.length || 0,
    next: handleLoadMore,
    hasMore: hasAfter,
    loader: /*#__PURE__*/React.createElement(Spinner, {
      s: {
        mt: 6
      },
      center: true
    })
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h1",
    s: {
      mb: 10
    },
    className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, t("CompanyManagement.companies")), /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    gap: 3,
    s: {
      mb: 8
    },
    template: "1fr 240px 3.3rem"
  }, /*#__PURE__*/React.createElement(InputText, {
    value: filter?.query || "",
    placeholder: t("CompanyManagement.search"),
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      color: theme.colors.text.secondary,
      src: "search",
      s: {
        mr: 2
      }
    }),
    color: "translucent",
    onChange: value => handleFilter({
      ...filter,
      query: value
    }),
    clearButton: true
  }), /*#__PURE__*/React.createElement(InputSelect, {
    value: sort,
    onChange: value => handleSort(value),
    options: lists.manageCompanies.sort.names,
    color: "translucent",
    fullWidth: true
  }), userHas(AuthoritiesEnum.INVITATIONS_WRITE) && /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("common.actions.create"),
    s: {
      p: 2.8
    },
    onClick: handleCreate,
    color: "translucent"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "plus",
    scale: 5
  }))), /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, !companies.length && status === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }) : companies.length ? /*#__PURE__*/React.createElement(Grid, {
    cols: 1,
    gap: 10,
    align: "stretch",
    equal: true
  }, companies.map(company => /*#__PURE__*/React.createElement(Card, {
    key: company.id,
    s: {
      p: 6
    },
    color: "background",
    shadow: true
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: "80px 1fr",
    gap: 6,
    s: {
      mb: 6
    }
  }, /*#__PURE__*/React.createElement(Picture, {
    src: company.logo,
    border: "rounded",
    width: "80px",
    height: "80px",
    name: company.name
  }), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "h2",
    s: {
      mb: 1
    }
  }, company.name), /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    display: "block",
    s: {
      mb: 1
    },
    weight: "semiBold"
  }, t("CompanyManagement.description")), company.description ? /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    s: {
      mb: 3
    },
    display: "block"
  }, company.description) : /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    s: {
      mb: 3
    },
    color: "textSecondary",
    display: "block"
  }, t("CompanyManagement.noDescription")), /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    display: "block",
    s: {
      mb: 1
    },
    weight: "semiBold"
  }, t("CompanyManagement.context")), company.context ? /*#__PURE__*/React.createElement(Markdown, {
    fontSize: 4,
    s: {
      mb: 3
    },
    display: "block",
    text: company.context
  }) : /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    s: {
      mb: 3
    },
    color: "textSecondary",
    display: "block"
  }, t("CompanyManagement.noContext")))), /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 7,
    template: "1fr 1fr 1fr minmax(0, 400px) minmax(0, 200px)",
    align: "stretch"
  }, /*#__PURE__*/React.createElement(Grid.Cell, {
    s: {
      px: 4,
      pt: 3,
      pb: 2
    }
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2
    }
  }, t("CompanyManagement.createdAt")), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4
  }, moment(company.createdAt).format("MMMM D, YYYY"))), /*#__PURE__*/React.createElement(Grid.Cell, {
    s: {
      px: 4,
      pt: 3,
      pb: 2
    }
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2
    }
  }, t("CompanyManagement.emailDomains")), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4
  }, company.emailDomains)), /*#__PURE__*/React.createElement(Grid.Cell, {
    s: {
      px: 4,
      pt: 3,
      pb: 2
    }
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2
    }
  }, t("CompanyManagement.authorities")), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "bold",
    fontSize: 4
  }, company.authorities.length, helpers.sensitiveAuthorities.some(authority => company.authorities.includes(authority)) && /*#__PURE__*/React.createElement(Badge, {
    color: "translucentDanger",
    s: {
      py: 1,
      px: 1,
      ml: 1
    },
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-html": `<strong>Sensitive authorities assigned:</strong><br/>${company.authorities.filter(authority => helpers.sensitiveAuthorities.includes(authority)).join("<br/>")}`
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "infoOutline",
    s: {
      mt: -0.5
    }
  })))), /*#__PURE__*/React.createElement(Grid.Cell, {
    s: {
      px: 4,
      pt: 3,
      pb: 2
    }
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2
    }
  }, t("CompanyManagement.roles")), company.roles.map(role => /*#__PURE__*/React.createElement(Button, {
    key: role.id,
    color: "primaryGrey",
    s: {
      px: 2,
      py: 0.5,
      mb: 1,
      mr: 1
    },
    display: "inline-block",
    fontSize: 3,
    rounded: true,
    outline: false,
    onClick: () => handleUpdateRole(company.id, role.id),
    style: {
      whiteSpace: "nowrap"
    },
    elemAfter: /*#__PURE__*/React.createElement(Button, {
      color: "transparent",
      onClick: e => {
        e.stopPropagation();
        handleDeleteRole(company.id, role.id);
      },
      display: "inline-block",
      s: {
        px: 0.5,
        py: 0,
        ml: 1
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: "cross",
      scale: 4
    }))
  }, role.name, " (", role.authorities?.length, ")", helpers.sensitiveAuthorities.some(authority => role.authorities?.includes(authority)) && /*#__PURE__*/React.createElement(Badge, {
    color: "translucentDanger",
    s: {
      py: 0.5,
      px: 0.5,
      ml: 1
    },
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-html": `<strong>Sensitive authorities assigned:</strong><br/>${role.authorities?.filter(authority => helpers.sensitiveAuthorities.includes(authority)).join("<br/>")}`
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "infoOutline",
    s: {
      mt: -0.5
    }
  }))))), /*#__PURE__*/React.createElement(Grid.Cell, {
    s: {
      px: 4,
      pt: 3,
      pb: 2
    }
  }, !!company.settings.recommendedIntegrations?.length && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2
    }
  }, t("CompanyManagement.recommendedIntegrations")), company.settings.recommendedIntegrations?.map(integration => /*#__PURE__*/React.createElement(Badge, {
    key: integration,
    color: "grey",
    display: "inline-block",
    s: {
      mr: 1,
      mb: 1,
      py: 0
    }
  }, lists.integrations.types.names[integration]))), /*#__PURE__*/React.createElement(Text, {
    element: "span",
    display: "block",
    fontSize: 3,
    color: "textSecondary",
    s: {
      mb: 2
    }
  }, t("CompanyManagement.integrations")), company.integrations.length ? company.integrations.map(integration => /*#__PURE__*/React.createElement(Badge, {
    key: integration,
    color: "primary",
    display: "inline-block",
    s: {
      mr: 1,
      mb: 1,
      py: 0
    }
  }, integration)) : "-")), !!company.members.length && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    equal: true,
    gap: 3,
    align: "stretch",
    s: {
      mb: 3,
      mt: 6
    }
  }, company.members.filter(member => !member.isTemporary).map(member => /*#__PURE__*/React.createElement(CompanyManagementMemberCard, {
    companyId: company.id,
    member: member,
    key: member.id,
    s: {
      p: 3
    },
    border: true
  })), company.invitations.filter(invitation => invitation.status !== InvitationStatusEnum.ACTIVE).map(invitation => /*#__PURE__*/React.createElement(Card, {
    key: invitation.id,
    border: true,
    s: {
      p: 3
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    gap: 2,
    template: "4rem 1fr"
  }, /*#__PURE__*/React.createElement(Badge, {
    fontSize: 4,
    rounded: true,
    s: {
      p: 5
    },
    color: "primaryGrey"
  }, "In"), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    fontSize: 3,
    color: "primary"
  }, lists.invitations.statuses.names[invitation.status], " as ", invitation.role.name), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "semiBold",
    fontSize: 4
  }, invitation.firstName), /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "semiBold",
    fontSize: 3
  }, invitation.email, " by ", invitation.createdBy.email)))))), /*#__PURE__*/React.createElement(Collapsable, {
    maxHeight: 0,
    type: CollapsableTypeEnum.BUTTON,
    collapsedText: "Show Temporary Members",
    expandedText: "Hide Temporary Members"
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h4",
    s: {
      mb: 3
    }
  }, "Temporary"), /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    equal: true,
    gap: 3,
    align: "stretch"
  }, company.members.filter(member => member.isTemporary).map(member => /*#__PURE__*/React.createElement(CompanyManagementMemberCard, {
    companyId: company.id,
    member: member,
    key: member.id,
    s: {
      p: 3
    },
    border: true
  }))))), /*#__PURE__*/React.createElement(Actions, {
    align: "center"
  }, !!company.settings?.isPaidPlan && /*#__PURE__*/React.createElement(Icon, {
    src: "wallet",
    s: {
      mr: 2
    },
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": "Is Paid Plan"
  }), !!company.settings?.sendSlackStatusReport && /*#__PURE__*/React.createElement(Picture, {
    width: "1rem",
    height: "1rem",
    src: lists.common.apps.logos(theme)[AppEnum.SLACK],
    s: {
      mr: 2
    },
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": "Send Slack Status Report"
  }), /*#__PURE__*/React.createElement(Badge, {
    color: "transparent"
  }, "Weight: ", company.weight), userHasOneOf([AuthoritiesEnum.MANAGE_COMPANIES_WRITE]) && /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    handlerProps: {
      s: {
        p: 2
      },
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      src: "dots-vertical",
      color: theme.colors.text.primary,
      display: "block"
    })
  }, _ref4 => {
    let {
      isOpen,
      onItemClick
    } = _ref4;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, userHasOneOf([AuthoritiesEnum.MANAGE_COMPANIES_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleEdit(company.id);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "pencil",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.edit")), userHasOneOf([AuthoritiesEnum.MANAGE_COMPANIES_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleCreateRole(company.id);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "people",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("CompanyManagement.addRole")), userHasOneOf([AuthoritiesEnum.MANAGE_COMPANIES_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleInviteMembers(company.id);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "user-plus",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("CompanyManagement.inviteMembers")), userHasOneOf([AuthoritiesEnum.MANAGE_COMPANIES_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleDuplicate(company.id);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "copy",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.duplicate")), userHasOneOf([AuthoritiesEnum.MANAGE_COMPANIES_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleDownloadLineItemActions(company.id);
      }
    }, lineItemActionsLoading ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }) : /*#__PURE__*/React.createElement(Icon, {
      src: "download",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("CompanyManagement.exportLineItemActions"), /*#__PURE__*/React.createElement(Icon, {
      src: "encrypted",
      color: "primary",
      s: {
        ml: 1
      }
    })), userHasOneOf([AuthoritiesEnum.MANAGE_COMPANIES_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleDownloadTopicTrendActions(company.id);
      }
    }, topicTrendActionsLoading ? /*#__PURE__*/React.createElement(Spinner, {
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }) : /*#__PURE__*/React.createElement(Icon, {
      src: "download",
      scale: 4.5,
      s: {
        mr: 2
      },
      color: "primary"
    }), t("CompanyManagement.exportTopicTrendActions"), /*#__PURE__*/React.createElement(Icon, {
      src: "encrypted",
      color: "primary",
      s: {
        ml: 1
      }
    })), userHasOneOf([AuthoritiesEnum.MANAGE_COMPANIES_WRITE]) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        handleDelete(company.id);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "bin",
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete")));
  }))))) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    fontSize: 5,
    align: "center",
    display: "block"
  }, t("CompanyManagement.noCompaniesFound")))), userHas(AuthoritiesEnum.MANAGE_COMPANIES_WRITE) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.CREATE
  }, /*#__PURE__*/React.createElement(ManageCompanyCreateModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.UPDATE
  }, /*#__PURE__*/React.createElement(ManageCompanyUpdateModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.DELETE
  }, /*#__PURE__*/React.createElement(ManageCompanyDeleteModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: action === ManageCompaniesActionEnum.CREATE_ROLE
  }, /*#__PURE__*/React.createElement(ManageCompanyRoleCreateModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: action === ManageCompaniesActionEnum.UPDATE_ROLE
  }, /*#__PURE__*/React.createElement(ManageCompanyRoleUpdateModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: action === ManageCompaniesActionEnum.DELETE_ROLE
  }, /*#__PURE__*/React.createElement(ManageCompanyRoleDeleteModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: action === ManageCompaniesActionEnum.DUPLICATE
  }, /*#__PURE__*/React.createElement(ManageCompanyDuplicateModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: action === ManageCompaniesActionEnum.INVITE_MEMBERS
  }, /*#__PURE__*/React.createElement(ManageCompanyInviteMembersModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: action === ManageCompaniesActionEnum.UPDATE_MEMBER
  }, /*#__PURE__*/React.createElement(ManageCompanyMemberUpdateModal, null)), /*#__PURE__*/React.createElement(Modal, {
    open: action === ManageCompaniesActionEnum.DELETE_MEMBER
  }, /*#__PURE__*/React.createElement(ManageCompanyMemberDeleteModal, null))));
};
export default /*#__PURE__*/React.memo(secured({
  permissions: AuthoritiesEnum.MANAGE_COMPANIES_READ,
  noAccess: () => /*#__PURE__*/React.createElement(RestrictedSection, null)
})(CompanyManagement), () => true);